import React, { useState, useCallback, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { Error, Info } from '@mui/icons-material';
import ReplayIcon from '@mui/icons-material/Replay';
import { Button, CircularProgress, Grid, IconButton, List, ListItem, Stack, Tooltip, Typography } from '@mui/material';
import { FadingTooltip, UPLOAD_FILE_STATUS_KEY } from '@cosmotech/ui';
import { Api } from '../../../../services/config/Api';
import { TwoActionsDialogService } from '../../../../services/twoActionsDialog/twoActionsDialogService';
import WorkspaceService from '../../../../services/workspace/WorkspaceService';
import { useDatasets } from '../../../../state/hooks/DatasetHooks';
import { useOrganizationId } from '../../../../state/hooks/OrganizationHooks';
import { useCurrentScenarioData } from '../../../../state/hooks/ScenarioHooks';
import { useWorkspaceId } from '../../../../state/hooks/WorkspaceHooks.js';
import { DatasetsUtils } from '../../../../utils';

const DEFAULT_VALUE = {
  id: null,
  name: '',
  content: null,
  file: null,
  status: UPLOAD_FILE_STATUS_KEY.EMPTY,
  isReverted: false,
};

export const GenericTableVector = ({ parameterData, context, parameterValue }) => {
  const { id: tableName } = parameterData;
  const workspaceId = useWorkspaceId();
  const scenarioData = useCurrentScenarioData();
  const scenarioMainDataset = scenarioData?.datasetList?.[0];
  const currentScenarioParametersValues = scenarioData?.parametersValues;
  const datasets = useDatasets();
  const organizationId = useOrganizationId();

  const [missingColumnsList, setMissingColumnsList] = useState([]);
  const [fileHandlingError, setFileHandlingError] = useState(null);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [areColumnsLoading, setAreColumnsLoading] = useState(true);
  const [fileExists, setFileExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { setValue } = useFormContext();

  const revertTable = (tableName) => {
    setMissingColumnsList([]);
    setFileHandlingError(null);
    try {
      setValue(tableName, { ...DEFAULT_VALUE, isReverted: true }, { shouldDirty: true });
    } catch (error) {
      console.error(error);
      setFileHandlingError('Une erreur est survenue lors de la restauration');
    }
  };

  const onRevertClick = async () => {
    const dialogProps = {
      id: 'revert-table-vector',
      labels: {
        title: 'Rétablir le tableau ?',
        body: 'Ceci remplacera vos données par les données initiales du dataset.',
        button1: 'Annuler',
        button2: 'Rétablir',
      },
      button2Props: {
        color: 'error',
      },
    };
    const result = await TwoActionsDialogService.openDialog(dialogProps);
    if (result === 2) {
      revertTable(tableName);
    }
  };

  useEffect(() => {
    const fetchAvailableColumns = async () => {
      if (!organizationId || !scenarioMainDataset || !tableName) {
        setAreColumnsLoading(false);
        return;
      }

      setAreColumnsLoading(true);
      try {
        const availableColumnsQuery = `
          MATCH(n: ${tableName}) 
          WITH collect(n) as nodes 
          WITH [x IN nodes WHERE x.Metadata = 'AvailableColumns' | x.Values][0] AS columns 
          RETURN columns
        `;

        const { data } = await Api.Datasets.twingraphQuery(organizationId, scenarioMainDataset, {
          query: availableColumnsQuery,
        });

        if (data[0]?.columns) {
          setAvailableColumns([...data[0].columns]);
          setFileExists(true);
        } else {
          setAvailableColumns([]);
          setFileExists(false);
        }
      } catch (error) {
        console.error('Error fetching available columns:', error);
        setAvailableColumns([]);
        setFileExists(false);
      } finally {
        setAreColumnsLoading(false);
      }
    };

    fetchAvailableColumns();
  }, [organizationId, scenarioMainDataset, tableName]);

  const checkRequiredColumnsInHeader = (fileColumns, datasetMetadata) => {
    const missingColumns = [];
    datasetMetadata.forEach((column) => {
      if (!fileColumns.includes(column)) missingColumns.push(column);
    });
    return missingColumns;
  };

  const downloadFileFromStorage = useCallback(
    (tableName) => {
      const storedDatasetId = parameterValue.id;
      if (storedDatasetId) {
        const dataset = datasets?.find((dataset) => dataset.id === storedDatasetId);
        const storageFilePath = DatasetsUtils.getStorageFilePathFromDataset(dataset);
        if (storageFilePath == null) return null;
        return WorkspaceService.downloadWorkspaceFile(organizationId, workspaceId, storageFilePath);
      } else {
        const storageFilePathForInitialValue = `dataset/${scenarioMainDataset}/${tableName}.csv`;
        return WorkspaceService.downloadWorkspaceFile(organizationId, workspaceId, storageFilePathForInitialValue);
      }
    },
    [parameterValue?.id, datasets, organizationId, scenarioMainDataset, workspaceId]
  );

  const downloadFile = async (tableName) => {
    setIsLoading(true);
    try {
      await downloadFileFromStorage(tableName);
    } catch (error) {
      console.error(error);
      setFileHandlingError('Une erreur est survenue lors du téléchargement du fichier');
    } finally {
      setIsLoading(false);
    }
  };

  const isTableUpdated = () => {
    const savedParam = currentScenarioParametersValues?.find((p) => p.parameterId === tableName);
    return !!savedParam?.value;
  };

  return (
    <div>
      <Grid container spacing={2} style={{ margin: '20px' }}>
        {tableName &&
          (!isLoading ? (
            <Controller
              name={tableName}
              key={tableName}
              defaultValue={DEFAULT_VALUE}
              render={({ field }) => {
                const { value, onChange } = field;
                return (
                  <Grid sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <Typography variant="body1" color="textSecondary">
                      <span>
                        {tableName}.csv
                        {isTableUpdated() && (
                          <FadingTooltip title="Fichier mis à jour" placement="top">
                            <span style={{ marginLeft: '4px', color: '#ff9800', fontWeight: 'bold' }}>*</span>
                          </FadingTooltip>
                        )}
                      </span>
                    </Typography>
                    <Button
                      data-cy="download-asset-class-data-button"
                      variant="contained"
                      color="primary"
                      disabled={
                        !tableName ||
                        value?.status === UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD ||
                        !fileExists ||
                        (value?.isReverted && isTableUpdated())
                      }
                      onClick={() => {
                        downloadFile(tableName);
                      }}
                    >
                      Télécharger le CSV
                    </Button>
                    <Button
                      data-cy="upload-asset-class-data-button"
                      variant="contained"
                      color="secondary"
                      component="label"
                      disabled={!tableName || !context.editMode || !fileExists}
                      onChange={async (event) => {
                        setMissingColumnsList([]);
                        setFileHandlingError(null);
                        setIsLoading(true);
                        const file = event.target.files[0];
                        event.target.value = null;
                        if (file.type !== 'text/csv') {
                          setFileHandlingError('Le fichier doit être en format .csv');
                          setIsLoading(false);
                          return;
                        }
                        const firstChunk = file.slice(0, 5120);
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const splitChunk = event.target.result.trim().split(/\s*[\r\n]+\s*/g);
                          const fileColumns = splitChunk[0].split(',');
                          const missingColumns = checkRequiredColumnsInHeader(fileColumns, availableColumns);
                          const fileToUpload = {
                            name: file.name,
                            status: UPLOAD_FILE_STATUS_KEY.READY_TO_UPLOAD,
                            file,
                          };
                          if (missingColumns.length === 0) {
                            onChange(fileToUpload);
                          } else {
                            setMissingColumnsList(missingColumns);
                          }
                          setIsLoading(false);
                        };
                        reader.readAsText(firstChunk);
                      }}
                    >
                      Charger un nouveau CSV
                      <input accept=".csv" style={{ display: 'none' }} id="upload-csv-file" type="file" />
                    </Button>
                    {isTableUpdated() && (
                      <Tooltip title="Revert">
                        <span>
                          <IconButton
                            data-cy="revert-table-data-button"
                            color="primary"
                            disabled={!context.editMode}
                            onClick={onRevertClick}
                          >
                            <ReplayIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {areColumnsLoading && <CircularProgress />}
                  </Grid>
                );
              }}
            />
          ) : (
            <CircularProgress />
          ))}
        {missingColumnsList.length > 0 && (
          <Grid item sx={{ alignSelf: 'right' }}>
            <Stack sx={{ flexDirection: 'row', gap: 1 }}>
              <Error color="error" />
              <Typography data-cy="missing-columns-message" variant="subtitle" color="error">
                Le fichier n&apos;est pas valide. Les colonnes suivantes sont obligatoires&nbsp;:
              </Typography>
            </Stack>

            <List sx={{ pt: 0, pl: 4 }}>
              {missingColumnsList.map((column) => (
                <ListItem
                  data-cy={column}
                  sx={{ color: (theme) => theme.palette.error.main }}
                  dense={true}
                  disablePadding={true}
                  key={column}
                >
                  {column}
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
        {fileHandlingError && (
          <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center', ml: 1 }}>
            <Error color="error" />
            <Typography data-cy="file-handling-error" variant="subtitle" color="error">
              {fileHandlingError}
            </Typography>
          </Stack>
        )}
        {!fileExists && !areColumnsLoading && (
          <Stack sx={{ flexDirection: 'row', gap: 1, alignItems: 'center', ml: 1 }}>
            <FadingTooltip title="Ce fichier n'existe pas dans le jeu de données.">
              <Info color="primary" />
            </FadingTooltip>
          </Stack>
        )}
      </Grid>
    </div>
  );
};

GenericTableVector.propTypes = {
  parameterData: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  parameterValue: PropTypes.any,
};
